import { absoluteCentre, marginBottom, marginLeft, marginRight, marginTop, media, typography } from "../../../common/styles";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, Button, Input, InputElement } from "../../../common/components";
import React, { useContext, useState } from "react";
import { ActionType } from "../../../AppDataProvider/actions";
import { applyOpacity } from "../../../common/styles/utils";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Mixpanel } from "../../../mixpanel";
import { PartRequired } from "../../../utils/types";
import styled from "styled-components";
import { useCalculateDiscountLazyQuery } from "../../../GeneratedGraphQL";
import { useTranslation } from "react-i18next";
type DiscountCodeProps = {
  onLoadingCallback: (loading: boolean) => void;
  totalPriceInPence: number;
};
export function DiscountCode({
  onLoadingCallback,
  totalPriceInPence
}: DiscountCodeProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  type DiscountCodeBookingData = PartRequired<BookingData, "organisation" | "service" | "userDetails">;
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const {
    service,
    userDetails,
    organisation,
    discountCode
  } = appData.bookingData as DiscountCodeBookingData;
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [discountCodeApplied, setDiscountCodeApplied] = useState(!!discountCode);
  const [discountCodeValue, setDiscountCodeValue] = useState("");
  const onDiscountCodeChange = (discountCode: string) => {
    setDiscountCodeValue(discountCode);
  };
  const [calculateDiscount, {
    loading
  }] = useCalculateDiscountLazyQuery({
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      onLoadingCallback(false);
      const discountCodeFound = data.discountCodeByCode;
      if (!discountCodeFound) {
        setError(t("errors.discountCodeNotFound"));
        Mixpanel.track({
          name: "Discount code failed",
          props: {
            discountCode: discountCodeValue,
            failureReason: "NOT_FOUND"
          }
        });
        return;
      }
      const discounts = discountCodeFound.calculatedDiscount.__typename === "CalculatedDiscounts" ? discountCodeFound.calculatedDiscount.discounts : [];
      const noDiscountReason = data.discountCodeByCode?.calculatedDiscount.__typename === "NoDiscount" ? data.discountCodeByCode?.calculatedDiscount.noDiscountReason : undefined;
      if (noDiscountReason) {
        switch (noDiscountReason) {
          case "EXPIRED":
            setError(t("errors.discountCodeExpired"));
            break;
          case "MAX_CLAIMS_REACHED":
          case "MAX_CLAIMS_PER_CUSTOMER_REACHED":
            setError(t("errors.discountCodeMaxClaims"));
            break;
          case "NOT_ACTIVE":
          default:
            setError(t("errors.discountCodeNotFound"));
            break;
        }
        Mixpanel.track({
          name: "Discount code failed",
          props: {
            discountCode: discountCodeValue,
            failureReason: noDiscountReason
          }
        });
        return;
      }
      if (discounts.length > 0) {
        setShowControls(false);
        setDiscountCodeApplied(true);
        setError(undefined);
        appDataDispatch({
          payload: {
            updatedData: {
              discountCode: discountCodeValue
            }
          },
          type: ActionType.APPLY_DISCOUNT_CODE
        });
      }
    },
    onError(error) {
      console.error("onError", {
        error
      });
      onLoadingCallback(false);
      setError(t("errors.genericDiscountCodeError"));
    }
  });
  if (loading) {
    return <LoadingPill />;
  }
  return <DiscountCodeContainer data-sentry-element="DiscountCodeContainer" data-sentry-component="DiscountCode" data-sentry-source-file="DiscountCode.tsx">
      {showControls && !discountCodeApplied && <DiscountCodeForm id="discountCodeForm" aria-label={t("discountCodeForm")} onSubmit={event => {
      event.preventDefault();
      if (!discountCodeValue) return;
      onLoadingCallback(true);
      calculateDiscount({
        variables: {
          code: discountCodeValue,
          email: userDetails.email,
          organisationId: organisation._id,
          servicePrices: [{
            price: totalPriceInPence,
            serviceId: service._id
          }]
        }
      });
    }}>
          <Label>{t("addDiscountCode")}</Label>
          <DiscountCodeControls>
            <DiscountCodeInput variant="text" id={"firstName"} aria-label={t("discountCodeAriaLabel")} error={error} required value={discountCodeValue} onChange={(event: React.ChangeEvent<InputElement>) => {
          onDiscountCodeChange(event.target.value);
        }} />

            <ApplyDiscountCodeButton type="submit" variant="secondary">
              {t("apply")}
            </ApplyDiscountCodeButton>
          </DiscountCodeControls>
        </DiscountCodeForm>}
      {discountCodeApplied && <AppliedDiscountCode>
          <CodeNameAndLabel>
            <Label>{t("discountCodeLabel")}</Label>
            <DiscountCodeName>{discountCode}</DiscountCodeName>
          </CodeNameAndLabel>
          <RemoveDiscountCode variant="destructive" onClick={() => {
        appDataDispatch({
          type: ActionType.REMOVE_DISCOUNT_CODE
        });
        onDiscountCodeChange("");
        setDiscountCodeApplied(false);
      }}>
            <RemoveFileIcon icon={faTrashAlt} /> {t("remove")}
          </RemoveDiscountCode>
        </AppliedDiscountCode>}
    </DiscountCodeContainer>;
}
const DiscountCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${marginTop.s}
  ${marginBottom.s}
`;
const DiscountCodeForm = styled.form`
  display: flex;
  flex-direction: column;
`;
const Label = styled.label`
  ${typography.bodyBold};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  &::first-letter {
    text-transform: capitalize;
  }
`;
const DiscountCodeControls = styled.div`
  display: flex;
  align-items: center;

  @media ${media.mobile} {
    flex-direction: column;
  }
`;
const DiscountCodeInput = styled(Input)``;
const ApplyDiscountCodeButton = styled(Button)`
  width: 30%;
  ${marginLeft.s}
  ${marginBottom.s}

  @media ${media.mobile} {
    width: 100%;
    margin: 0px;
  }
`;
const AppliedDiscountCode = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  @media ${media.mobile} {
    flex-direction: column;
  }
`;
const CodeNameAndLabel = styled.div`
  display: flex;
`;
const DiscountCodeName = styled.div`
  ${typography.bodyBold}
  ${marginLeft.s}
  /*
   * A distinctive brand-accent highlight for the applied code itself,
   * matching the "accent marks a distinctive, minority-use highlight"
   * pattern already used for BookingInfo's occurrence count — makes the
   * confirmation ("your code XYZ is applied") visually pop rather than
   * reading as plain bold text.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-accent"]};
`;
const RemoveDiscountCode = styled(Button).attrs({
  type: "button"
})`
  width: 160px;
  text-transform: capitalize;

  @media ${media.mobile} {
    width: 100%;
    ${marginTop.m}
  }
`;
const RemoveFileIcon = styled(FontAwesomeIcon)`
  ${marginRight.s}
`;
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
export default DiscountCode;